// src/timing/motion-profile.js

import {
    toInternalDistanceLinear,
    toInternalAccelLinear,
    toInternalSpeedLinear,
    fromInternalSpeedLinear,
    toInternalAngleRotary,
    toInternalAccelRotary,
    toInternalSpeedRotary,
    fromInternalSpeedRotary
} from "./units.js";

export function isFinitePositive(v) {
    return typeof v === "number" && isFinite(v) && v > 0;
}

export function isFiniteNonZero(v) {
    return typeof v === "number" && isFinite(v) && v !== 0;
}

export function computeMotionCycleTime(motion, axis, units) {
    const errors = [];
    if (!isFiniteNonZero(motion.distance)) errors.push("距離が未入力");
    if (!isFinitePositive(motion.accel)) errors.push("加速度が未入力");
    if (!isFinitePositive(motion.decel)) errors.push("減速度が未入力");
    if (!isFinitePositive(motion.maxSpeed)) errors.push("最高速度が未入力");
    if (errors.length) return { error: errors.join(", ") };

    let distance, accel, decel, maxSpeed;
    if (axis.motionType === "linear") {
        distance = toInternalDistanceLinear(motion.distance, units);
        accel = toInternalAccelLinear(motion.accel, units);
        decel = toInternalAccelLinear(motion.decel, units);
        maxSpeed = toInternalSpeedLinear(motion.maxSpeed, units);
    } else {
        distance = toInternalAngleRotary(motion.distance, units);
        accel = toInternalAccelRotary(motion.accel, units);
        decel = toInternalAccelRotary(motion.decel, units);
        maxSpeed = toInternalSpeedRotary(motion.maxSpeed, units);
    }

    // Use absolute distance for time calculations; direction is sign only
    const absDist = Math.abs(distance);
    const accelTime = maxSpeed / accel;
    const decelTime = maxSpeed / decel;
    const accelDistance = 0.5 * accel * accelTime * accelTime;
    const decelDistance = 0.5 * decel * decelTime * decelTime;
    const totalVariableDistance = accelDistance + decelDistance;

    let adjustedMaxSpeed = maxSpeed;
    let cruiseTime = 0;
    let cycleTime = 0;
    let actualAccelTime = accelTime;
    let actualDecelTime = decelTime;

    if (totalVariableDistance > absDist) {
        adjustedMaxSpeed = Math.sqrt(2 * absDist * accel * decel / (accel + decel));
        actualAccelTime = adjustedMaxSpeed / accel;
        actualDecelTime = adjustedMaxSpeed / decel;
        cycleTime = actualAccelTime + actualDecelTime;
    } else {
        const cruiseDistance = absDist - totalVariableDistance;
        cruiseTime = cruiseDistance / adjustedMaxSpeed;
        cycleTime = accelTime + cruiseTime + decelTime;
    }
    return {
        cycleTime,
        accelTime: actualAccelTime,
        cruiseTime,
        decelTime: actualDecelTime,
        adjustedMaxSpeed
    };
}

export function calculateMotionProfilePoints(motion, axis, times, units, timeStep = 0.005) {
    const points = [];
    const { startSec, accelTime, cruiseTime, decelTime, adjustedMaxSpeed } = times;
    let internalAccel, internalDecel;
    if (axis.motionType === "linear") {
        internalAccel = toInternalAccelLinear(motion.accel, units);
        internalDecel = toInternalAccelLinear(motion.decel, units);
    } else {
        internalAccel = toInternalAccelRotary(motion.accel, units);
        internalDecel = toInternalAccelRotary(motion.decel, units);
    }
    const totalMotion = accelTime + cruiseTime + decelTime;
    
    // start gap zero point
    points.push({ x: startSec, y: 0 });
    
    for (let t = 0; t <= totalMotion; t += timeStep) {
        let v;
        if (t <= accelTime) {
            v = internalAccel * t;
        } else if (t <= accelTime + cruiseTime) {
            v = adjustedMaxSpeed;
        } else {
            const dt = t - accelTime - cruiseTime;
            v = adjustedMaxSpeed - internalDecel * dt;
            if (v < 0) v = 0;
        }
        const display = axis.motionType === "linear" ? fromInternalSpeedLinear(v, units) : fromInternalSpeedRotary(v, units);
        points.push({ x: startSec + t, y: display });
    }
    points.push({ x: startSec + totalMotion, y: 0 });
    if (times.waitTime > 0) {
        points.push({ x: startSec + totalMotion + times.waitTime, y: 0 });
    }
    return points;
}
